import { useEffect, useMemo, useState } from 'react'
import Icon from '../Icon'
import { api, useContent, useLive, type Inbound, type LiveState, type RunInfo, type Tally } from '../../lib/classroom'
import { L, w, lang } from '../../lib/words'
import { celebrate, sound } from '../../lib/fun'
import { correctText, isAnswerable } from '../../../shared/classroom'
import { StepBody } from './Step'
import { Board } from './LiveStudent'
import Pichi from './Pichi'
import LanguageToggle from '../LanguageToggle'

/**
 * The projector. Opened by the teacher on the laptop plugged into the screen
 * while they drive from their phone or tablet — it joins the room as the
 * teacher but only displays. The join QR stays in the corner the whole class.
 */
export default function Present({ runId }: { runId: string }) {
  const modules = useContent()
  const [state, setState] = useState<LiveState | null>(null)
  const [tally, setTally] = useState<Tally | null>(null)
  const [code, setCode] = useState<string | null>(null)
  const [ended, setEnded] = useState<RunInfo['board'] | null>(null)
  const [lastCall, setLastCall] = useState<string | null>(null)

  useEffect(() => { void api.run(runId).then((r) => setCode(r.code ?? null)).catch(() => {}) }, [runId])

  const onMessage = (m: Inbound) => {
    if (m.t === 'state') {
      setState(m)
      const key = m.callOn ? `${m.callOn.userId}:${m.step}` : null
      if (key && key !== lastCall) { setLastCall(key); sound('callon') }
    } else if (m.t === 'tally') setTally(m)
    else if (m.t === 'ended') { setEnded(m.summary?.board ?? []); celebrate(); sound('level') }
  }
  useLive(runId, onMessage)

  const lesson = useMemo(() => {
    for (const m of modules ?? []) for (const l of m.lessons) if (l.id === state?.lessonId) return l
    return null
  }, [modules, state?.lessonId])

  if (ended) {
    return (
      <div className="present present-end">
        <Pichi size={180} stage={3} mood="cheer" />
        <h1>{w('leaderboard')}</h1>
        <Board board={ended} />
      </div>
    )
  }
  if (!state || !lesson) return <div className="present"><p className="boot">{w('connecting')}</p></div>
  const step = lesson.steps[state.step]
  if (!step) return null
  const answers = Object.values(tally?.answers ?? {})

  return (
    <div className="present">
      <header className="present-top">
        <span>{L(lesson.title)}</span>
        <span className="row">{state.step + 1}/{state.total} <LanguageToggle /></span>
      </header>

      <main className="present-main">
        <StepBody step={step} big />
        {isAnswerable(step) && step.type !== 'discuss' && (
          <div className="present-choices">
            {step.type === 'question' && step.kind === 'yesno' && [true, false].map((v) => (
              <div key={String(v)} className={`pchoice ${v ? 'c2' : 'c0'}${state.reveal && step.answer === v ? ' good' : ''}`}>
                {v ? w('yes') : w('no')}
                {state.reveal && <b>{answers.filter((a) => 'value' in a.value && a.value.value === v).length}</b>}
              </div>
            ))}
            {((step.type === 'question' && step.kind === 'choice') || step.type === 'poll') && step.choices.map((c, i) => (
              <div key={i} className={`pchoice c${i % 6}${state.reveal && step.type === 'question' && step.answer === i ? ' good' : ''}`}>
                <span className="choice-letter">{String.fromCharCode(65 + i)}</span> {L(c)}
                {state.reveal && <b>{answers.filter((a) => 'value' in a.value && a.value.value === i).length}</b>}
              </div>
            ))}
            {state.reveal && step.type === 'question' && (
              <p className="present-answer"><Icon name="check" size={36} /> {correctText(step, lang())}</p>
            )}
          </div>
        )}
      </main>

      <div className="present-foot">
        <span />
        {isAnswerable(step)
          ? <span className="present-count"><b>{state.answered}</b> {w('of')} {state.present} {w('answered')}</span>
          : <span className="present-count"><Icon name="people" size={34} /> <b>{state.present}</b> {w('connected')}</span>}
        {code && (
          <span className="present-join">
            <span>{w('joinAt')} <b className="host">{location.host}</b><br />{w('withCode')}</span>
            <b className="code">{code}</b>
          </span>
        )}
      </div>

      {state.callOn && (
        <div className="callon-overlay present-call">
          <div className="callon-card">
            <Pichi size={150} stage={2} mood="cheer" />
            <h1>{w('yourTurn')}</h1>
            <p className="called-name">{state.callOn.name}</p>
          </div>
        </div>
      )}
    </div>
  )
}
